import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import WomensPage from './pages/WomensPage';
import WomenProductDetailPage from './pages/WomenProductDetailPage';
import MensPage from './pages/MensPage';
import MenProductDetailPage from './pages/MenProductDetailPage';
import KidsPage from './pages/KidsPage';
import KidsProductDetailPage from './pages/KidsProductDetailPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import PaymentResultPage from './pages/PaymentResultPage';
import PaymentQRPage from './pages/PaymentQRPage';
import WishlistPage from './pages/WishlistPage';
import OrdersPage from './pages/OrdersPage';
import OrderTrackPage from './pages/OrderTrackPage';
import SearchPage from './pages/SearchPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminSignupPage from './pages/AdminSignupPage';
import WalletPage from './pages/WalletPage';
import AboutPage from './pages/AboutPage';
import PrivacyPolicyPage from './pages/policy/PrivacyPolicyPage';
import RefundPolicyPage from './pages/policy/RefundPolicyPage';
import CancellationPolicyPage from './pages/policy/CancellationPolicyPage';
import TermsPage from './pages/policy/TermsPage';
import ReturnRefundPage from './pages/policy/ReturnRefundPage';
import ShippingPolicyPage from './pages/policy/ShippingPolicyPage';
import { AuthProvider } from './contexts/AuthContext';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Admin routes — no nav/footer Layout */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/signup" element={<AdminSignupPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />

            {/* All user-facing routes wrapped in Layout */}
            <Route path="/*" element={
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/women" element={<WomensPage />} />
                  <Route path="/women/products/:id" element={<WomenProductDetailPage />} />
                  <Route path="/men" element={<MensPage />} />
                  <Route path="/men/products/:id" element={<MenProductDetailPage />} />
                  <Route path="/kids" element={<KidsPage />} />
                  <Route path="/kids/products/:id" element={<KidsProductDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/payment/result" element={<PaymentResultPage />} />
                  <Route path="/payment/qr" element={<PaymentQRPage />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/wallet" element={<WalletPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/policy/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/policy/refund" element={<RefundPolicyPage />} />
                  <Route path="/policy/cancellation" element={<CancellationPolicyPage />} />
                  <Route path="/policy/terms" element={<TermsPage />} />
                  <Route path="/policy/return" element={<ReturnRefundPage />} />
                  <Route path="/policy/shipping" element={<ShippingPolicyPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/orders/track/:id" element={<OrderTrackPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
